import AccountApi from "@/api/Account";
import SettingsApi from "@/api/Settings";

import {
  UseInfiniteQueryOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { AccountApiNS } from "~types/api/account";
import { SettingsApiNS } from "~types/api/settings";

import SendContactUsFormArgs = SettingsApiNS.SendContactUsFormArgs;

import GetUsersListArgs = AccountApiNS.GetUsersListArgs;

import GetUsersListResponse = AccountApiNS.GetUsersListResponse;

enum Keys {
  ContactUs = "contact-us",
  UsersList = "users-list"
}

export const useSettings = () => {
  const queryClient = useQueryClient();

  const useSendContactUsForm = useMutation(
    (data: SendContactUsFormArgs) => SettingsApi.sendContactUsForm(data),
    {
      onMutate: async data => {
        await queryClient.cancelQueries([Keys.ContactUs]);
        const previousProfileData = await queryClient.getQueryData([
          Keys.ContactUs
        ]);
        queryClient.setQueryData([Keys.ContactUs], data);
        return { previousProfileData };
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData(
          [Keys.ContactUs],
          context?.previousProfileData
        );
        toast.error("Can't send data. Check network connection");
      }
    }
  );

  const useUsersList = (
    params?: GetUsersListArgs,
    options?: UseQueryOptions
  ) => {
    return useInfiniteQuery(
      [Keys.UsersList, params],
      ({ pageParam = 1 }) =>
        AccountApi.getUsersList({ ...params, page: pageParam }),
      {
        getPreviousPageParam: firstPage => firstPage?.previous || undefined,
        getNextPageParam: lastPage => lastPage?.next || undefined,
        ...options
      } as UseInfiniteQueryOptions<GetUsersListResponse>
    );
  };

  return {
    sendContactUs: useSendContactUsForm,
    getUsersList: useUsersList
  } as const;
};
